/**
 * Iter 235 — Modal "Dossier d'enquête IA" sur fiche prospect/client.
 *
 * Lance une enquête web + Pappers + synthèse Claude, puis affiche dans 5 onglets :
 *  - Synthèse (résumé + niveau confiance + signaux risque + faits clés)
 *  - Société (Pappers : SIREN, dirigeants, capital, NAF, adresse, site, téléphone)
 *  - Presse (articles trouvés via Tavily)
 *  - Réseaux sociaux (LinkedIn + autres profils détectés)
 *  - Risques (BODACC + procédures collectives)
 *
 * Coût estimé affiché avant lancement (~0.12€). Cache 30 jours.
 */
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  Loader2, Search, X, ExternalLink, AlertCircle, ShieldCheck, ShieldAlert,
  Building2, Users, Newspaper, Linkedin, Sparkles, RefreshCw, Clock,
} from "lucide-react";
import { toast } from "sonner";

const POLL_INTERVAL_MS = 2000;

export default function IntelAgentModal({ prospectId, prospectName, open, onClose }) {
  const { axiosAuth } = useAuth();
  const [tab, setTab] = useState("summary");
  const [investigation, setInvestigation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [status, setStatus] = useState(null);

  const loadLast = useCallback(async () => {
    if (!prospectId) return;
    setLoading(true);
    try {
      const [s, last] = await Promise.all([
        axiosAuth().get("/intel/status").catch(() => ({ data: { configured: false } })),
        axiosAuth().get(`/intel/prospects/${prospectId}/last`),
      ]);
      setStatus(s.data);
      if (last.data?.available) {
        setInvestigation(last.data);
      } else {
        setInvestigation(null);
      }
    } finally {
      setLoading(false);
    }
  }, [axiosAuth, prospectId]);

  useEffect(() => {
    if (open) loadLast();
  }, [open, loadLast]);

  const startInvestigation = async (force = false) => {
    setStarting(true);
    try {
      const r = await axiosAuth().post(`/intel/investigate/${prospectId}`, { force });
      if (r.data.from_cache) {
        setInvestigation(r.data);
        setStarting(false);
        toast.success("Résultat en cache (< 30 jours)");
        return;
      }
      const investigationId = r.data.investigation_id;
      // Poll
      const poll = setInterval(async () => {
        try {
          const p = await axiosAuth().get(`/intel/investigations/${investigationId}`);
          setInvestigation(p.data);
          if (p.data.status === "completed" || p.data.status === "failed") {
            clearInterval(poll);
            setStarting(false);
            if (p.data.status === "completed") {
              toast.success(`Enquête terminée (~${p.data.estimated_cost_eur?.toFixed?.(2) || "0.12"}€)`);
            } else {
              toast.error("Enquête échouée : " + (p.data.error || "?"));
            }
          }
        } catch (e) {
          clearInterval(poll);
          setStarting(false);
          toast.error("Erreur de polling");
        }
      }, POLL_INTERVAL_MS);
      // Safety timeout 90s
      setTimeout(() => clearInterval(poll), 90000);
    } catch (e) {
      setStarting(false);
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="bg-[#14101c] border border-white/10 rounded-2xl w-full max-w-4xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="intel-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-cyan-500/15 flex items-center justify-center">
              <Search className="w-5 h-5 text-cyan-300" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100">Dossier d'enquête IA</h3>
              <p className="text-xs text-slate-400">{prospectName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/5 rounded-lg text-slate-400" data-testid="intel-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>
          ) : !status?.configured ? (
            <NotConfiguredState status={status} />
          ) : starting || investigation?.status === "in_progress" ? (
            <RunningState investigation={investigation} status={status} />
          ) : !investigation ? (
            <EmptyState status={status} onStart={() => startInvestigation(false)} starting={starting} />
          ) : (
            <ResultView
              investigation={investigation}
              tab={tab}
              setTab={setTab}
              onRefresh={() => startInvestigation(true)}
              starting={starting}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── States ─────────────────────────────────────────────


function NotConfiguredState({ status }) {
  return (
    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-5 text-sm text-amber-200" data-testid="intel-not-configured">
      <div className="flex items-center gap-2 mb-3">
        <AlertCircle className="w-5 h-5" />
        <span className="font-semibold">Agent IA non configuré</span>
      </div>
      <p className="text-xs text-slate-300 mb-3">Pour activer l'agent enquêteur, l'admin doit configurer 2 clés API (Tavily + Pappers).</p>
      {status?.instructions && (
        <ol className="text-xs text-slate-300 space-y-1 list-decimal list-inside">
          {status.instructions.map((s, i) => <li key={i}>{s}</li>)}
        </ol>
      )}
      <p className="mt-3 text-xs text-slate-400">→ Paramètres → API → Agent IA enquêteur</p>
    </div>
  );
}


function EmptyState({ status, onStart, starting }) {
  const quota = status?.quota || {};
  return (
    <div className="text-center p-8" data-testid="intel-empty-state">
      <div className="w-16 h-16 mx-auto bg-cyan-500/15 rounded-2xl flex items-center justify-center mb-4">
        <Search className="w-8 h-8 text-cyan-300" />
      </div>
      <h4 className="text-lg font-bold text-slate-100 mb-2">Aucune enquête pour ce prospect</h4>
      <p className="text-sm text-slate-400 mb-6 max-w-md mx-auto">
        L'agent va fouiller le web (Tavily), interroger Pappers (SIREN, BODACC, dirigeants), puis synthétiser via Claude Sonnet 4.5 en gérant les homonymes.
      </p>
      <div className="bg-[#0b0811] border border-white/5 rounded-lg p-4 mb-4 max-w-md mx-auto text-left">
        <div className="text-xs text-slate-400 mb-2">📊 Coût et temps estimés</div>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div><span className="text-slate-500">Coût</span> <span className="text-amber-400 font-mono">~{(status?.estimated_cost_per_investigation_eur || 0.12).toFixed(2)}€</span></div>
          <div><span className="text-slate-500">Durée</span> <span className="text-cyan-300">~30s</span></div>
          <div className="col-span-2"><span className="text-slate-500">Quota mensuel</span> <span className="text-slate-100">{quota.used_this_month || 0} / {quota.monthly_quota || 50}</span> ({quota.remaining || 50} restantes)</div>
        </div>
      </div>
      <button
        onClick={onStart}
        disabled={starting || (quota.remaining ?? 1) <= 0}
        className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold rounded-lg disabled:opacity-50"
        data-testid="intel-start-btn"
      >
        {starting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
        Lancer l'enquête
      </button>
      {(quota.remaining ?? 1) <= 0 && (
        <p className="mt-2 text-xs text-rose-400">⚠️ Quota mensuel atteint</p>
      )}
    </div>
  );
}


function RunningState({ investigation, status }) {
  const progress = investigation?.progress || 0;
  const phase = investigation?.phase || "starting";
  const phaseLabels = {
    starting: "Initialisation...",
    tavily_search: "Recherche web (Tavily)...",
    pappers_company: "Vérification SIREN (Pappers)...",
    pappers_details: "Récupération dirigeants + BODACC...",
    llm_synthesis: "Synthèse IA (Claude Sonnet 4.5)...",
    completed: "Terminé !",
  };
  return (
    <div className="text-center p-8" data-testid="intel-running">
      <Loader2 className="w-12 h-12 mx-auto text-cyan-400 animate-spin mb-4" />
      <h4 className="text-lg font-bold text-slate-100 mb-2">Enquête en cours</h4>
      <p className="text-sm text-cyan-300 mb-4">{phaseLabels[phase] || phase}</p>
      <div className="w-full max-w-md mx-auto bg-white/5 rounded-full h-2 mb-2">
        <div className="bg-cyan-400 h-2 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
      </div>
      <p className="text-xs text-slate-500">{progress}%</p>
    </div>
  );
}


// ─── Result tabs ────────────────────────────────────────


function ResultView({ investigation, tab, setTab, onRefresh, starting }) {
  const result = investigation.result || {};
  const ageDays = investigation.age_days;

  const tabs = [
    { id: "summary", label: "Synthèse", icon: Sparkles },
    { id: "company", label: "Société", icon: Building2 },
    { id: "press", label: "Presse", icon: Newspaper },
    { id: "social", label: "Social", icon: Linkedin },
    { id: "risks", label: "Risques", icon: ShieldAlert },
  ];

  return (
    <div data-testid="intel-result">
      {/* Cache freshness banner */}
      {ageDays != null && (
        <div className="flex items-center justify-between bg-[#0b0811] border border-white/5 rounded-lg px-3 py-2 mb-4">
          <div className="text-xs text-slate-400 flex items-center gap-2">
            <Clock className="w-3 h-3" />
            {ageDays === 0 ? "Enquête récente" : `Cache de ${ageDays} jour${ageDays > 1 ? "s" : ""}`}
            {investigation.from_cache && <span className="text-emerald-400 ml-1">· résultat instantané (gratuit)</span>}
          </div>
          <button onClick={onRefresh} disabled={starting}
            className="text-xs px-3 py-1 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 rounded-lg flex items-center gap-1 disabled:opacity-50"
            data-testid="intel-refresh-btn">
            {starting ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Rafraîchir (~0.12€)
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-white/5 mb-4 overflow-x-auto">
        {tabs.map((t) => {
          const Ic = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 whitespace-nowrap ${tab === t.id ? "border-cyan-400 text-cyan-300" : "border-transparent text-slate-400 hover:text-slate-200"}`}
              data-testid={`intel-tab-${t.id}`}>
              <Ic className="w-3.5 h-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {tab === "summary" && <SummaryTab result={result} />}
      {tab === "company" && <CompanyTab result={result} />}
      {tab === "press" && <PressTab result={result} />}
      {tab === "social" && <SocialTab result={result} />}
      {tab === "risks" && <RisksTab result={result} />}
    </div>
  );
}


function SummaryTab({ result }) {
  const conf = result.homonym_confidence || 0;
  const confColor = conf >= 0.8 ? "text-emerald-400" : conf >= 0.5 ? "text-amber-400" : "text-rose-400";
  const riskColor = { low: "text-emerald-400", medium: "text-amber-400", high: "text-rose-400" }[result.risk_level] || "text-slate-400";
  return (
    <div className="space-y-4" data-testid="intel-summary-tab">
      <div className="bg-[#0b0811] border border-white/5 rounded-xl p-4">
        <div className="text-[10px] uppercase tracking-wider text-cyan-400 font-bold mb-2">Synthèse IA</div>
        <p className="text-sm text-slate-200 leading-relaxed">{result.summary || "Aucune synthèse"}</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-[#0b0811] border border-white/5 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
            <ShieldCheck className="w-3 h-3" /> Confiance identité
          </div>
          <div className={`text-2xl font-bold ${confColor}`}>{Math.round(conf * 100)}%</div>
          {result.homonym_notes && <p className="text-[11px] text-slate-400 mt-1">{result.homonym_notes}</p>}
        </div>
        <div className="bg-[#0b0811] border border-white/5 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
            <ShieldAlert className="w-3 h-3" /> Niveau de risque
          </div>
          <div className={`text-2xl font-bold capitalize ${riskColor}`}>{result.risk_level || "—"}</div>
        </div>
      </div>
      {(result.key_findings || []).length > 0 && (
        <div className="bg-[#0b0811] border border-white/5 rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Faits clés confirmés</div>
          <ul className="space-y-1.5">
            {result.key_findings.map((f, i) => (
              <li key={i} className="text-xs text-slate-200 flex items-start gap-2">
                <span className="text-cyan-400 mt-0.5">✓</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


function CompanyTab({ result }) {
  const c = result.company_intel;
  if (!c) return <p className="text-sm text-slate-400 p-4 text-center">Aucune donnée société trouvée. Renseignez le champ "Société" sur la fiche prospect pour activer la recherche Pappers.</p>;
  const fields = [
    { l: "SIREN", v: c.siren },
    { l: "SIRET siège", v: c.siret_siege },
    { l: "Dénomination", v: c.denomination || c.nom_entreprise },
    { l: "Forme juridique", v: c.forme_juridique },
    { l: "Capital", v: c.capital ? `${c.capital} €` : null },
    { l: "Création", v: c.date_creation },
    { l: "Activité", v: c.domaine_activite },
    { l: "Code NAF", v: c.code_naf },
    { l: "Effectif", v: c.tranche_effectif },
    { l: "Adresse", v: c.siege?.adresse_ligne_1 ? `${c.siege.adresse_ligne_1}, ${c.siege.code_postal} ${c.siege.ville}` : null },
    { l: "Site web", v: c.site_web },
    { l: "Téléphone", v: c.telephone },
    { l: "Email", v: c.email },
    { l: "Statut RCS", v: c.statut_rcs },
  ];
  return (
    <div className="space-y-4" data-testid="intel-company-tab">
      <div className="grid grid-cols-2 gap-2">
        {fields.filter((f) => f.v).map((f, i) => (
          <div key={i} className="bg-[#0b0811] border border-white/5 rounded-lg p-2.5">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{f.l}</div>
            <div className="text-xs text-slate-100 font-mono break-all">{f.v}</div>
          </div>
        ))}
      </div>
      {(result.dirigeants || []).length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
            <Users className="w-3 h-3" /> Dirigeants
          </div>
          <div className="space-y-1.5">
            {result.dirigeants.map((d, i) => (
              <div key={i} className="bg-[#0b0811] border border-white/5 rounded-lg p-2.5 flex justify-between">
                <div>
                  <div className="text-sm text-slate-100 font-medium">{d.nom}</div>
                  <div className="text-[11px] text-slate-400">{d.fonction}</div>
                </div>
                {d.depuis && <div className="text-[11px] text-slate-500">depuis {d.depuis}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function PressTab({ result }) {
  const news = result.news_results || [];
  if (news.length === 0) return <p className="text-sm text-slate-400 p-4 text-center">Aucun article de presse trouvé.</p>;
  return (
    <div className="space-y-2" data-testid="intel-press-tab">
      {news.map((n, i) => (
        <a key={i} href={n.url} target="_blank" rel="noreferrer"
          className="block bg-[#0b0811] border border-white/5 hover:border-cyan-500/30 rounded-lg p-3 transition-colors">
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="text-sm font-medium text-slate-100 flex-1">{n.title}</div>
            <ExternalLink className="w-3 h-3 text-slate-400 mt-1 shrink-0" />
          </div>
          <div className="text-[11px] text-slate-400 truncate">{n.url}</div>
          {n.snippet && <p className="text-xs text-slate-300 mt-2 leading-relaxed">{n.snippet}</p>}
        </a>
      ))}
    </div>
  );
}


function SocialTab({ result }) {
  const social = result.social_profiles || [];
  const linkedin = result.linkedin_results || [];
  const all = [
    ...social,
    ...linkedin.filter((l) => !social.some((s) => s.url === l.url)).map((l) => ({ platform: "linkedin", url: l.url, title: l.title, snippet: l.snippet, confidence: l.score })),
  ];
  if (all.length === 0) return <p className="text-sm text-slate-400 p-4 text-center">Aucun profil social détecté.</p>;
  return (
    <div className="space-y-2" data-testid="intel-social-tab">
      {all.map((p, i) => (
        <a key={i} href={p.url} target="_blank" rel="noreferrer"
          className="block bg-[#0b0811] border border-white/5 hover:border-cyan-500/30 rounded-lg p-3 transition-colors">
          <div className="flex items-center gap-2 mb-1">
            <Linkedin className="w-3.5 h-3.5 text-cyan-300" />
            <span className="text-[10px] uppercase text-cyan-400 font-bold">{p.platform || "lien"}</span>
            {p.confidence != null && (
              <span className={`text-[10px] ml-auto ${p.confidence >= 0.7 ? "text-emerald-400" : p.confidence >= 0.4 ? "text-amber-400" : "text-rose-400"}`}>
                {Math.round(p.confidence * 100)}% match
              </span>
            )}
          </div>
          <div className="text-sm font-medium text-slate-100">{p.title}</div>
          <div className="text-[11px] text-slate-400 truncate">{p.url}</div>
          {p.snippet && <p className="text-xs text-slate-300 mt-1.5 line-clamp-2">{p.snippet}</p>}
        </a>
      ))}
    </div>
  );
}


function RisksTab({ result }) {
  const procedures = result.procedures || [];
  const bodacc = result.bodacc || [];
  const signals = result.risk_signals || [];
  if (procedures.length === 0 && bodacc.length === 0 && signals.length === 0) {
    return <p className="text-sm text-slate-400 p-4 text-center">✓ Aucun risque détecté.</p>;
  }
  return (
    <div className="space-y-4" data-testid="intel-risks-tab">
      {signals.length > 0 && (
        <div className="bg-rose-500/5 border border-rose-500/20 rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-wider text-rose-400 font-bold mb-2">⚠️ Signaux IA</div>
          <ul className="space-y-1">
            {signals.map((s, i) => <li key={i} className="text-xs text-slate-200">• {s}</li>)}
          </ul>
        </div>
      )}
      {procedures.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Procédures collectives</div>
          {procedures.map((p, i) => (
            <div key={i} className="bg-rose-500/5 border border-rose-500/20 rounded-lg p-3 mb-2">
              <div className="text-sm text-rose-300 font-medium">{p.type}</div>
              {p.description && <div className="text-xs text-slate-400 mt-1">{p.description}</div>}
            </div>
          ))}
        </div>
      )}
      {bodacc.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Publications BODACC ({bodacc.length})</div>
          <div className="space-y-1.5">
            {bodacc.slice(0, 10).map((p, i) => (
              <div key={i} className="bg-[#0b0811] border border-white/5 rounded-lg p-2.5">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-slate-100">{p.type}</span>
                  <span className="text-[10px] text-slate-500">{p.date}</span>
                </div>
                {p.description && <p className="text-[11px] text-slate-400 line-clamp-2">{p.description}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
